import type { MouseEvent } from 'react';

import BpkButton, { BUTTON_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-button';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import Account from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/account-circle';
import { BpkSpinner, SPINNER_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-spinner';

import ProfilePicture from './ProfilePicture';
import getLoginButtonVariantCopy from './getLoginButtonVariantCopy';

import STYLES from './LoginButton.module.scss';

type Props = {
  onLoginClick: (e: MouseEvent<HTMLButtonElement>) => void;
  a11yLabel: string;
  isLoggedIn?: boolean;
  profilePictureUrl: string | null;
  onProfilePictureError: () => void;
  accountText: string;
  loginText: string;
  loginButtonVariant?: string;
};

const AccountIcon = withRtlSupport(Account);

const MobileLoginButton = ({
  a11yLabel,
  accountText,
  isLoggedIn,
  loginButtonVariant,
  loginText,
  onLoginClick,
  onProfilePictureError,
  profilePictureUrl,
}: Props) => {
  // Same as desktop: while userLoggedIn is still resolving, show a spinner rather than
  // guessing logged-out, otherwise the button would flash from logged-out copy/icon to the
  // gravatar for any user who turns out to be logged in.
  if (isLoggedIn === undefined) {
    return (
      <BpkSpinner className={STYLES.AccountSpinner} type={SPINNER_TYPES.light} alignToButton />
    );
  }

  // Same as desktop: when logged in, show the traveller's gravatar, falling back to the
  // account icon if there is no picture / it fails to load.
  if (isLoggedIn) {
    return (
      <ProfilePicture
        profilePictureUrl={profilePictureUrl}
        onProfilePictureError={onProfilePictureError}
        onLoginClick={onLoginClick}
        accountText={accountText}
      />
    );
  }

  // GATE-1359: mWeb login button variants experiment.
  const variantCopy = getLoginButtonVariantCopy(loginButtonVariant, loginText);

  if (variantCopy) {
    return (
      <BpkButton
        type={BUTTON_TYPES.secondaryOnDark}
        aria-label={variantCopy}
        onClick={onLoginClick}
        className={STYLES.MobileLoginButton}
        label={variantCopy}
      >
        {variantCopy}
      </BpkButton>
    );
  }

  return (
    <BpkButton
      type={BUTTON_TYPES.linkOnDark}
      aria-label={a11yLabel}
      onClick={onLoginClick}
      className={STYLES.MobileLoginButton}
      iconOnly
    >
      <AccountIcon />
    </BpkButton>
  );
};

export default MobileLoginButton;
